import { useState } from 'react';
import { MessageCircle, User, Clock, ChevronDown, ChevronUp, LogIn, Loader2, Send, MoreHorizontal, Pencil, Trash2, Check, X, Reply, CornerDownRight } from 'lucide-react';
import {
  useGetCommentsByAdQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useReactToCommentMutation,
  useAppSelector,
  selectIsAuthenticated,
  selectCurrentUser,
} from '@nimbazaar/store';
import { useToast } from './Toast';
import { EmojiReactions } from './EmojiReactions';

interface CommentSectionProps {
  adId: string;
  onLoginClick: () => void;
}

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

interface CommentData {
  id: string;
  content: string;
  parentId?: string | null;
  createdAt: string;
  user: { id: string; name: string; avatar?: string };
  replies?: CommentData[];
  reactions?: { id: string; emoji: string; userId: string }[];
}

interface CommentItemProps {
  comment: CommentData;
  adId: string;
  isOwner: boolean;
  isAuthenticated: boolean;
  onReply: (commentId: string, userName: string) => void;
  isReply?: boolean;
}

function CommentItem({ comment, adId, isOwner, isAuthenticated, onReply, isReply }: CommentItemProps) {
  const toast = useToast();
  const currentUser = useAppSelector(selectCurrentUser);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [updateComment, { isLoading: isUpdating }] = useUpdateCommentMutation();
  const [reactToComment] = useReactToCommentMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

  const handleEdit = async () => {
    const trimmed = editContent.trim();
    if (!trimmed) {
      toast.error('Error', 'Comment cannot be empty.');
      return;
    }
    if (trimmed === comment.content) {
      setIsEditing(false);
      return;
    }
    try {
      await updateComment({ id: comment.id, adId, content: trimmed }).unwrap();
      toast.success('Updated', 'Comment updated successfully.');
      setIsEditing(false);
    } catch {
      toast.error('Error', 'Failed to update comment.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteComment({ id: comment.id, adId }).unwrap();
      toast.success('Deleted', 'Comment deleted successfully.');
    } catch {
      toast.error('Error', 'Failed to delete comment.');
    }
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  return (
    <div className={`group flex gap-3 ${isReply ? 'ml-4 sm:ml-8 mt-3' : ''}`}>
      {/* Reply indicator */}
      {isReply && (
        <CornerDownRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-2" />
      )}

      {/* Avatar */}
      {comment.user.avatar ? (
        <img
          src={comment.user.avatar}
          alt={comment.user.name}
          className={`${isReply ? 'w-7 h-7' : 'w-9 h-9'} rounded-full object-cover flex-shrink-0`}
        />
      ) : (
        <div className={`${isReply ? 'w-7 h-7' : 'w-9 h-9'} rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0`}>
          <User className={`${isReply ? 'w-3 h-3' : 'w-4 h-4'} text-primary-500`} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 text-sm">{comment.user.name}</span>
          <span className="flex items-center gap-1 text-gray-400 text-xs">
            <Clock className="w-3 h-3" />
            {formatRelativeTime(comment.createdAt)}
          </span>

          {/* Owner Menu */}
          {isOwner && !isEditing && !showDeleteConfirm && (
            <div className="relative ml-auto">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 rounded transition-all"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>

              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-28 sm:w-32">
                    <button
                      onClick={() => { setIsEditing(true); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => { setShowDeleteConfirm(true); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Edit Mode */}
        {isEditing ? (
          <div className="mt-1.5 flex gap-2">
            <input
              type="text"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              disabled={isUpdating}
              maxLength={1000}
              className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm disabled:bg-gray-50"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEdit();
                if (e.key === 'Escape') handleCancelEdit();
              }}
            />
            <button
              onClick={handleEdit}
              disabled={isUpdating || !editContent.trim()}
              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
              title="Save"
            >
              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button
              onClick={handleCancelEdit}
              disabled={isUpdating}
              className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : showDeleteConfirm ? (
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Delete this comment?</span>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              className="px-3 py-1 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              No
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isDeleting && <Loader2 className="w-3 h-3 animate-spin" />}
              Yes
            </button>
          </div>
        ) : (
          <>
            <p className="text-gray-600 text-sm mt-0.5 whitespace-pre-line break-words">
              {comment.content}
            </p>
            {/* Reactions + Reply */}
            <div className="flex items-center gap-3 mt-1.5">
              {isAuthenticated && (
                <EmojiReactions
                  reactions={comment.reactions || []}
                  currentUserId={currentUser?.id}
                  onReact={(emoji) => reactToComment({ id: comment.id, adId, emoji })}
                />
              )}
              {!isAuthenticated && (comment.reactions?.length ?? 0) > 0 && (
                <EmojiReactions
                  reactions={comment.reactions || []}
                  onReact={() => {}}
                />
              )}
              {!isReply && isAuthenticated && (
                <button
                  onClick={() => onReply(comment.id, comment.user.name)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary-500 transition-colors"
                >
                  <Reply className="w-3 h-3" />
                  Reply
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function CommentSection({ adId, onLoginClick }: CommentSectionProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectCurrentUser);
  const toast = useToast();

  const [showAll, setShowAll] = useState(false);
  const [content, setContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<{ id: string; userName: string } | null>(null);

  const { data, isLoading: isLoadingComments } = useGetCommentsByAdQuery({ adId, page: 1, limit: 50 });
  const [createComment, { isLoading: isSubmitting }] = useCreateCommentMutation();

  const comments = (data?.data || []) as CommentData[];
  const total = data?.meta?.total || 0;
  const displayedComments = showAll ? comments : comments.slice(0, 5);

  const handleReply = (commentId: string, userName: string) => {
    setReplyingTo({ id: commentId, userName });
    setContent('');
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setContent('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = content.trim();

    if (!trimmed) {
      toast.error('Empty Comment', 'Please write something before posting.');
      return;
    }
    if (trimmed.length > 1000) {
      toast.error('Too Long', 'Comment must be 1000 characters or less.');
      return;
    }

    try {
      await createComment({
        adId,
        content: trimmed,
        parentId: replyingTo?.id,
      }).unwrap();
      setContent('');
      setReplyingTo(null);
      toast.success(replyingTo ? 'Reply Posted!' : 'Comment Posted!', replyingTo ? 'Your reply has been added.' : 'Your comment has been added.');
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string } } };
      toast.error('Error', apiError?.data?.error?.message || 'Failed to post comment.');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-3 sm:p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-secondary-500" />
        Comments
        {total > 0 && (
          <span className="text-sm font-normal text-gray-500">({total})</span>
        )}
      </h2>

      {/* Comment List */}
      {isLoadingComments ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex gap-3">
              <div className="w-9 h-9 bg-gray-200 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-32" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <MessageCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No comments yet</p>
          <p className="text-sm mt-1">Be the first to comment!</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {displayedComments.map((comment) => (
              <div key={comment.id}>
                <CommentItem
                  comment={comment}
                  adId={adId}
                  isOwner={currentUser?.id === comment.user.id}
                  isAuthenticated={isAuthenticated}
                  onReply={handleReply}
                />
                {/* Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="space-y-0">
                    {comment.replies.map((reply) => (
                      <CommentItem
                        key={reply.id}
                        comment={reply}
                        adId={adId}
                        isOwner={currentUser?.id === reply.user.id}
                        isAuthenticated={isAuthenticated}
                        onReply={handleReply}
                        isReply
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {comments.length > 5 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-primary-500 hover:text-primary-600 font-medium text-sm flex items-center justify-center gap-1"
            >
              {showAll ? (
                <>
                  Show Less
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Show All {comments.length} Comments
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </>
      )}

      {/* Comment Form */}
      <div className="border-t border-gray-100 mt-4 pt-4">
        {!isAuthenticated ? (
          <div className="text-center">
            <button
              onClick={onLoginClick}
              className="inline-flex items-center gap-2 px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors text-sm"
            >
              <LogIn className="w-4 h-4" />
              Login to Comment
            </button>
          </div>
        ) : (
          <>
            {/* Reply indicator */}
            {replyingTo && (
              <div className="flex items-center gap-2 mb-2 text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-lg">
                <Reply className="w-3.5 h-3.5" />
                <span>Replying to <strong>{replyingTo.userName}</strong></span>
                <button
                  onClick={cancelReply}
                  className="ml-auto text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={replyingTo ? `Reply to ${replyingTo.userName}...` : 'Add a comment...'}
                disabled={isSubmitting}
                maxLength={1000}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={isSubmitting || !content.trim()}
                className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm font-medium"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {replyingTo ? 'Reply' : 'Post'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
